// Notifications Cache Manager (v2)
// ─────────────────────────────────────────────────────────────────────────
// Same stale-while-revalidate idea as before, but generalized to hold one
// independent, paginated "window" per tab/filter instead of a single flat
// list of everything. This is what lets each tab show cached data instantly
// on mount while a background fetch (if stale) refreshes just that tab.
//
// A window key is either a category ("follow" | "reaction" | "messages" |
// "general") or a specific type ("type:comment", "type:post", ...) used by
// the general tab's dropdown filter.
// ─────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'notificationsCacheV2';
const LEGACY_KEY = '__legacy__';

const emptyWindow = () => ({ items: [], hasMore: true, oldestTimestamp: null, timestamp: null });

class NotificationsCache {
  constructor() {
    const loaded = this.loadFromStorage();
    this.userId = loaded?.userId || null;
    this.windows = loaded?.windows || {}; // { [key]: { items, hasMore, oldestTimestamp, timestamp } }
    this.unreadCounts = loaded?.unreadCounts || { total: 0 };
    this.listeners = new Set();
  }

  loadFromStorage() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Error loading notifications cache', e);
      return null;
    }
  }

  saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        userId: this.userId,
        windows: this.windows,
        unreadCounts: this.unreadCounts,
      }));
    } catch (e) {
      console.error('Error saving notifications cache', e);
    }
  }

  _resetIfDifferentUser(userId) {
    if (this.userId !== userId) {
      this.userId = userId;
      this.windows = {};
      this.unreadCounts = { total: 0 };
    }
  }

  // ── Per-window (per-tab) reads/writes ───────────────────────────────────

  getWindow(userId, key) {
    if (this.userId !== userId) return null;
    const win = this.windows[key];
    return win ? { ...win, items: [...win.items] } : null;
  }

  // Replace the first page of a window (fresh load / live update).
  setFirstPage(userId, key, { items, hasMore, oldestTimestamp }) {
    this._resetIfDifferentUser(userId);
    this.windows[key] = { items, hasMore, oldestTimestamp, timestamp: Date.now() };
    this.saveToStorage();
    this.notifyListeners();
  }

  // Append an older page onto an existing window (infinite scroll).
  appendPage(userId, key, { items, hasMore, oldestTimestamp }) {
    if (this.userId !== userId) return;
    const existing = this.windows[key] || emptyWindow();
    const existingIds = new Set(existing.items.map((n) => n.id));
    const merged = [...existing.items, ...items.filter((n) => !existingIds.has(n.id))];
    this.windows[key] = { items: merged, hasMore, oldestTimestamp, timestamp: Date.now() };
    this.saveToStorage();
    this.notifyListeners();
  }

  // Apply a targeted update (read/delete/new) to every window that contains
  // this notification id — a notification can appear in a category window
  // and, if the general dropdown filter is active, a type window too.
  updateNotificationEverywhere(userId, notificationId, updates) {
    if (this.userId !== userId) return;
    let changed = false;
    Object.keys(this.windows).forEach((key) => {
      const win = this.windows[key];
      const idx = win.items.findIndex((n) => n.id === notificationId);
      if (idx !== -1) {
        win.items = win.items.map((n) => (n.id === notificationId ? { ...n, ...updates } : n));
        changed = true;
      }
    });
    if (changed) {
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  removeNotificationEverywhere(userId, notificationId) {
    if (this.userId !== userId) return;
    let changed = false;
    Object.keys(this.windows).forEach((key) => {
      const win = this.windows[key];
      if (win.items.some((n) => n.id === notificationId)) {
        win.items = win.items.filter((n) => n.id !== notificationId);
        changed = true;
      }
    });
    if (changed) {
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  removeManyEverywhere(userId, notificationIds) {
    if (this.userId !== userId) return;
    const idSet = new Set(notificationIds);
    let changed = false;
    Object.keys(this.windows).forEach((key) => {
      const win = this.windows[key];
      if (win.items.some((n) => idSet.has(n.id))) {
        win.items = win.items.filter((n) => !idSet.has(n.id));
        changed = true;
      }
    });
    if (changed) {
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  markManyReadEverywhere(userId, notificationIds, { preserveHighlight = false } = {}) {
    if (this.userId !== userId) return;
    const idSet = new Set(notificationIds);
    let changed = false;
    Object.keys(this.windows).forEach((key) => {
      const win = this.windows[key];
      if (win.items.some((n) => idSet.has(n.id))) {
        win.items = win.items.map((n) => (idSet.has(n.id) ? {
          ...n,
          read: true,
          highlighted: preserveHighlight ? true : false,
        } : n));
        changed = true;
      }
    });
    if (changed) {
      this.saveToStorage();
      this.notifyListeners();
    }
  }

  // ── Unread counts ────────────────────────────────────────────────────────

  getUnreadCounts(userId) {
    if (this.userId !== userId) return { total: 0 };
    return { ...this.unreadCounts };
  }

  setUnreadCounts(userId, counts) {
    this._resetIfDifferentUser(userId);
    this.unreadCounts = counts;
    this.saveToStorage();
    this.notifyListeners();
  }

  // ── Freshness helpers (per window) ──────────────────────────────────────

  isValid(userId, keyOrTtl, maybeTtl) {
    const legacyCall = typeof keyOrTtl === 'number';
    const key = legacyCall ? LEGACY_KEY : keyOrTtl;
    const ttl = legacyCall ? keyOrTtl : (maybeTtl ?? 5 * 60 * 1000);
    if (this.userId !== userId) return false;
    const win = this.windows[key];
    if (!win?.timestamp) return false;
    return (Date.now() - win.timestamp) < ttl;
  }

  isStale(userId, keyOrTtl, maybeTtl) {
    const legacyCall = typeof keyOrTtl === 'number';
    const key = legacyCall ? LEGACY_KEY : keyOrTtl;
    const ttl = legacyCall ? keyOrTtl : (maybeTtl ?? 5 * 60 * 1000);
    if (this.userId !== userId) return false;
    const win = this.windows[key];
    if (!win?.timestamp) return false;
    return (Date.now() - win.timestamp) >= ttl;
  }

  // ── Legacy flat-list API ─────────────────────────────────────────────────
  // Kept for any other code (e.g. a global real-time NotificationListener)
  // still using the pre-refactor contract: get/set the whole list at once,
  // append one new notification, look it up/remove it by id. These all
  // operate on a dedicated "legacy" window so they don't fight with the
  // per-tab windows NotificationsPage uses, while still routing through
  // the same update/remove helpers so a change made via one API is
  // reflected everywhere.

  get(userId) {
    const win = this.getWindow(userId, LEGACY_KEY);
    return win ? { notifications: win.items, timestamp: win.timestamp } : null;
  }

  set(userId, notifications) {
    this.setFirstPage(userId, LEGACY_KEY, { items: notifications, hasMore: false, oldestTimestamp: null });
  }

  addNotification(userId, notification) {
    this._resetIfDifferentUser(userId);
    const win = this.windows[LEGACY_KEY] || emptyWindow();
    const idx = win.items.findIndex((n) => n.id === notification.id);
    win.items = idx !== -1
      ? win.items.map((n) => (n.id === notification.id ? { ...n, ...notification } : n))
      : [notification, ...win.items];
    win.timestamp = Date.now();
    this.windows[LEGACY_KEY] = win;

    // Also reflect into whichever per-tab window(s) already hold this id,
    // so NotificationsPage and any legacy listener never disagree.
    Object.keys(this.windows).forEach((key) => {
      if (key === LEGACY_KEY) return;
      const w = this.windows[key];
      if (w.items.some((n) => n.id === notification.id)) {
        w.items = w.items.map((n) => (n.id === notification.id ? { ...n, ...notification } : n));
      }
    });

    this.saveToStorage();
    this.notifyListeners();
  }

  removeNotification(userId, notificationId) {
    this.removeNotificationEverywhere(userId, notificationId);
  }

  updateNotification(userId, notificationId, updates) {
    this.updateNotificationEverywhere(userId, notificationId, updates);
  }

  // ── Subscriptions ────────────────────────────────────────────────────────

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners() {
    this.listeners.forEach((cb) => cb({ userId: this.userId, windows: this.windows, unreadCounts: this.unreadCounts }));
  }

  clear() {
    this.userId = null;
    this.windows = {};
    this.unreadCounts = { total: 0 };
    this.saveToStorage();
    this.notifyListeners();
  }
}

const notificationsCache = new NotificationsCache();
export default notificationsCache;
